import { Link } from 'react-router-dom'
import DocsSidebar from '../components/DocsSidebar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import '../styles/docs.css'

const QUICK_STARTS = [
  'We need a production LLM workflow for support, sales, research, or internal operations.',
  'We want one governed API for OpenAI, Anthropic, Gemini, and local models.',
  'We need an FDE team to turn an AI prototype into a shipped product.',
  'We need evals, logs, runbooks, and cost controls before launch.',
]

const CAPABILITIES = [
  {
    intent: 'Forward deployed engineering',
    ask: 'Scope a business workflow, design the AI system, and ship the first production path.',
    result: 'A senior ShimmerTech team embeds with your stakeholders, builds a vertical slice, and transfers the operating playbook.',
    skill: 'FDE team',
  },
  {
    intent: 'LLM API gateway',
    ask: 'Put multiple model providers behind one governed API.',
    result: 'ShimmerTech provides routing, structured outputs, retries, rate limits, logs, provider flexibility, and usage controls.',
    skill: 'LLM gateway',
  },
  {
    intent: 'Evaluation and quality loops',
    ask: 'Catch prompt drift, schema failures, regression risk, and bad model responses.',
    result: 'We add eval datasets, review workflows, failure envelopes, and release gates around model-powered features.',
    skill: 'Evals',
  },
  {
    intent: 'GPU and inference infrastructure',
    ask: 'Serve models, queue workloads, and run inference without a fragile one-off script.',
    result: 'We design endpoints, workers, autoscaling plans, observability, and runbooks for production inference workloads.',
    skill: 'AI infra',
  },
  {
    intent: 'Agentic workflows',
    ask: 'Give teams agents that read data, call tools, write reports, and leave auditable traces.',
    result: 'ShimmerTech builds agent workflows with scoped tools, logging, approval boundaries, and human handoff points.',
    skill: 'Agents',
  },
  {
    intent: 'Production operations',
    ask: 'Make the AI system observable, maintainable, and safe after launch.',
    result: 'We deliver runbooks, telemetry, cost controls, incident paths, architecture notes, and team enablement.',
    skill: 'Operations',
  },
]

export default function OpendeployCapabilitiesDocs() {
  return (
    <>
      <SEO
        title="What ShimmerTech Provides"
        description="A guide to ShimmerTech FDE teams, LLM APIs, evals, GPU inference, agent workflows, and production AI operations."
        path="/docs/what-ShimmerTech-can-do"
      />
      <div className="od-docs-page">
        <SiteNav />
        <div className="od-docs-frame od-docs-frame--article">
          <DocsSidebar />

          <main className="od-docs-article">
            <nav className="od-docs-breadcrumb" aria-label="Breadcrumb">
              <Link to="/docs">Docs</Link>
              <span>/</span>
              <span>What ShimmerTech provides</span>
            </nav>

            <header id="overview" className="od-docs-article-hero">
              <p className="od-docs-kicker">FDE and LLM</p>
              <h1>What ShimmerTech provides</h1>
              <p>
                ShimmerTech is a forward-deployed engineering and LLM infrastructure company.
                We help teams design, ship, and operate production AI systems with embedded
                builders, governed model APIs, evals, inference infrastructure, and runbooks.
              </p>
              <div className="od-docs-meta">
                <span>Updated July 16, 2026</span>
                <span>{CAPABILITIES.length} service areas</span>
                <span>Contact: hi@shimmertech.net</span>
              </div>
            </header>

            <section id="examples" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Examples</p>
                <h2>Start with the business problem</h2>
                <p>Tell us the workflow, model risk, data boundary, and launch target. ShimmerTech turns that into a production path.</p>
              </div>

              <div className="od-docs-install-card">
                <div className="od-docs-install-commands" role="list">
                  {QUICK_STARTS.map((command, index) => (
                    <div className="od-docs-install-command-row" role="listitem" key={command}>
                      <span className="od-docs-install-command-index" aria-hidden="true">
                        {index + 1}
                      </span>
                      <code>{command}</code>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="skills" className="od-docs-article-section">
              <div className="od-docs-section-copy">
                <p className="od-docs-kicker">Service map</p>
                <h2>Where we help</h2>
                <p>These are the core ways ShimmerTech works with teams building production AI systems.</p>
              </div>

              <div className="od-docs-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>When you need</th>
                      <th>Ask for</th>
                      <th>What happens</th>
                      <th>Area</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CAPABILITIES.map((item) => (
                      <tr key={item.skill}>
                        <td>{item.intent}</td>
                        <td>{item.ask}</td>
                        <td>{item.result}</td>
                        <td>{item.skill}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </main>

          <aside className="od-docs-toc" aria-label="On this page">
            <p>On this page</p>
            <a href="#overview">Overview</a>
            <a href="#examples">Examples</a>
            <a href="#skills">Skill map</a>
          </aside>
        </div>
        <Footer />
      </div>
    </>
  )
}
